import { useState, useCallback } from "react";
import { BookOpen, Sparkles, Save, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function AdminEssayEditor({ topicId, api }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadEssays = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/topics/${topicId}/essays`, { timeout: 90000 });
      setA(data.side_a_essay || "");
      setB(data.side_b_essay || "");
      setLoaded(true);
    } catch {
      toast.error("Couldn't load arguments");
    } finally {
      setLoading(false);
    }
  }, [api, topicId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) loadEssays();
  };

  const regenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/topics/${topicId}/essays/generate`, {}, { timeout: 90000 });
      setA(data.side_a_essay || "");
      setB(data.side_b_essay || "");
      toast.success("AI draft regenerated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!a.trim() || !b.trim()) { toast.error("Both sides need text"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/topics/${topicId}/essays`, { side_a_essay: a, side_b_essay: b });
      toast.success("Arguments saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const wc = (t) => t.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 text-xs font-semibold text-neutral hover:text-dark transition-colors"
        data-testid={`essay-editor-toggle-${topicId}`}
      >
        <BookOpen className="w-3.5 h-3.5" /> Full arguments (long-form)
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`essay-editor-${topicId}`}>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-neutral py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading / generating arguments…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-bold text-view-a uppercase tracking-wider">View A essay</span>
                    <span className="text-[10px] text-neutral">{wc(a)} words</span>
                  </div>
                  <textarea value={a} onChange={(e) => setA(e.target.value)} rows={8} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-a/30 resize-y leading-relaxed" data-testid={`essay-a-textarea-${topicId}`} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-bold text-view-b uppercase tracking-wider">View B essay</span>
                    <span className="text-[10px] text-neutral">{wc(b)} words</span>
                  </div>
                  <textarea value={b} onChange={(e) => setB(e.target.value)} rows={8} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-view-b/30 resize-y leading-relaxed" data-testid={`essay-b-textarea-${topicId}`} />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={regenerate} disabled={generating} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-view-a bg-view-a-light rounded-lg hover:bg-view-a/20 transition-colors disabled:opacity-50" data-testid={`essay-regenerate-${topicId}`}>
                  {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {generating ? "Generating…" : "Regenerate with AI"}
                </button>
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-dark rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50" data-testid={`essay-save-${topicId}`}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
